import { useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Access token lives in memory only — never localStorage
let _accessToken = null;

export function getAccessToken() { return _accessToken; }
export function setAccessToken(t) { _accessToken = t; }

// Decode JWT payload without verifying signature (client-side, read-only)
function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

export function useAuth({ onLogin, onLogout } = {}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refreshTimer = useRef(null);

  // Schedule silent token refresh at 80% of token lifetime
  function scheduleRefresh(token) {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const exp = decodeJwtExp(token);
    if (!exp) return;
    const remaining = exp - Date.now();
    if (remaining <= 0) { silentRefresh(); return; }
    // Refresh at 80% of remaining time (e.g. 12min for a 15min token)
    const delay = Math.max(remaining * 0.8, 10_000);
    refreshTimer.current = setTimeout(() => silentRefresh(), delay);
  }

  async function silentRefresh() {
    try {
      const res = await fetch(`${API}/api/auth/refresh`, { method: "POST", credentials: "include" });
      if (!res.ok) { logout(); return; }
      const { accessToken } = await res.json();
      _accessToken = accessToken;
      scheduleRefresh(accessToken);
    } catch {
      // Network error — keep existing token until it expires
    }
  }

  // Auto-login on mount: try to refresh from httpOnly cookie
  useEffect(() => {
    (async () => {
      // Check URL fragment for token from Google OAuth redirect
      const hash = window.location.hash;
      if (hash.includes("token=")) {
        const params = new URLSearchParams(hash.slice(1));
        const token = params.get("token");
        if (token) {
          _accessToken = token;
          window.history.replaceState(null, "", window.location.pathname);
          await fetchMe();
          setLoading(false);
          return;
        }
      }

      // Try cookie-based refresh
      try {
        const res = await fetch(`${API}/api/auth/refresh`, { method: "POST", credentials: "include" });
        if (res.ok) {
          const { accessToken } = await res.json();
          _accessToken = accessToken;
          scheduleRefresh(accessToken);
          await fetchMe();
        }
      } catch {}
      setLoading(false);
    })();
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchMe() {
    if (!_accessToken) return;
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${_accessToken}` },
        credentials: "include",
      });
      if (!res.ok) return;
      const u = await res.json();
      setUser(u);
      onLogin?.(u);
    } catch {}
  }

  const login = useCallback(async (emailOrUser, password, rememberMe = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: emailOrUser, password, rememberMe }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Login failed");
        setLoading(false);
        return false;
      }
      _accessToken = data.accessToken;
      scheduleRefresh(data.accessToken);
      setUser(data.user);
      onLogin?.(data.user);
      setLoading(false);
      return true;
    } catch {
      setError("Network error — please try again");
      setLoading(false);
      return false;
    }
  }, [onLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: _accessToken ? { Authorization: `Bearer ${_accessToken}` } : {},
      });
    } catch {}
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    _accessToken = null;
    setUser(null);
    onLogout?.();
  }, [onLogout]);

  const forgotPassword = useCallback(async (emailAddr) => {
    const res = await fetch(`${API}/api/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailAddr }),
    });
    return res.ok;
  }, []);

  return { user, loading, error, login, logout, forgotPassword, setError };
}
